import type { Opportunity } from '../../shared/opportunity';
import type { OriginKind } from './home';
import type { TravelTimes } from './travelTimes';
import { eventPoint, minutesTo, type LatLng, type TravelCity, type TravelMode } from './walkshed';

/**
 * The travel line on a card (contract §6, §17): "25 min by transit". Read off
 * the same `TravelTimes` the filter uses, so a card never says 25 minutes about
 * an event the 20-minute filter kept. "≈" only when the minutes are an
 * estimate (TravelTime unavailable). Origin to event only, never event to event.
 */

export type { TravelMode, LatLng } from './walkshed';
export { travelMinutes } from './walkshed';

export interface TravelEstimate {
  minutes: number;
  mode: TravelMode;
  /** Measured from a neighborhood's centroid rather than the event's own address. */
  approximate: boolean;
}

export function estimateTravel(
  origin: LatLng,
  event: Opportunity,
  mode: TravelMode,
  city: TravelCity,
): TravelEstimate | null {
  const at = eventPoint(event, city);
  if (!at) return null;
  return { minutes: minutesTo(origin, at.point, mode, city), mode, approximate: at.approximate };
}

/** The card's estimate from whichever source is in use; null when the event has nowhere to measure to. */
export function estimateFrom(times: TravelTimes, event: Opportunity): TravelEstimate | null {
  const minutes = times.minutes(event);
  if (minutes == null) return null;
  return { minutes, mode: times.mode, approximate: times.approximate(event) };
}

/** "by transit", "by bike", "walk": what follows the minutes. */
export const MODE_PHRASE: Record<TravelMode, string> = {
  walk: 'walk',
  bike: 'by bike',
  transit: 'by transit',
};

export const MODE_LABEL: Record<TravelMode, string> = {
  walk: 'Walk',
  bike: 'Bike',
  transit: 'Transit',
};

/** "25 min", "1 hr 20 min". */
export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
}

/** "≈ 25 min by transit", "12 min walk", "Over 1 hr 30 min by bike". */
export function travelLabel(t: TravelEstimate): string {
  const approx = t.approximate ? '≈ ' : '';
  // Beyond what TravelTime was asked to look: all it knows is "further".
  if (!Number.isFinite(t.minutes)) return `${approx}Over ${formatMinutes(BAND_EDGES.at(-1)!)} ${MODE_PHRASE[t.mode]}`;
  // A centroid can be where home is; nobody gets anywhere in no time.
  return `${approx}${formatMinutes(Math.max(1, t.minutes))} ${MODE_PHRASE[t.mode]}`;
}

/** Where Nearest first puts its headings, in minutes; beyond the last is "Over 1 hr 30 min". */
const BAND_EDGES = [15, 30, 45, 60, 90];

/**
 * Nearest first's heading for a travel time, in place of the day: "15–30 min"
 * over "by transit". Null is the "Distance unknown" group's.
 */
export function travelBand(minutes: number | null, mode: TravelMode): { key: string; title: string; subtitle: string } {
  if (minutes == null) return { key: 'unknown', title: 'Distance unknown', subtitle: 'No address to measure to' };
  const i = BAND_EDGES.findIndex((edge) => minutes <= edge);
  const subtitle = MODE_PHRASE[mode];
  if (i === 0) return { key: 'band:0', title: `Within ${formatMinutes(BAND_EDGES[0])}`, subtitle };
  if (i === -1) return { key: `band:${BAND_EDGES.length}`, title: `Over ${formatMinutes(BAND_EDGES.at(-1)!)}`, subtitle };
  const from = BAND_EDGES[i - 1];
  const to = BAND_EDGES[i];
  // "15–30 min", "45 min – 1 hr": the unit once where both ends share it.
  const title = to < 60 ? `${from}\u2013${to} min` : `${formatMinutes(from)} \u2013 ${formatMinutes(to)}`;
  return { key: `band:${i}`, title, subtitle };
}

/** "41 events within 30 min", "190 events at any distance". */
export function countSentence(count: number, minutes: number | null): string {
  const events = `${count} ${count === 1 ? 'event' : 'events'}`;
  return minutes == null ? `${events} at any distance` : `${events} within ${formatMinutes(minutes)}`;
}

/** "Gowanus, Brooklyn" → "Gowanus"; "350 5th Avenue, Manhattan…" → "350 5th Avenue". */
export const shortPlace = (label: string) => label.split(',')[0].trim() || label;

/**
 * "within 30 min by transit of home": the results bar's addition. When the
 * minutes are estimates it says so, and from what (contract §17).
 */
export function withinPhrase(
  minutes: number,
  mode: TravelMode,
  from: OriginKind,
  { addressLabel, times }: { addressLabel?: string; times?: TravelTimes | null } = {},
): string {
  const of = from === 'current' ? 'where you are' : from === 'address' && addressLabel ? shortPlace(addressLabel) : 'home';
  const base = `within ${formatMinutes(minutes)} ${MODE_PHRASE[mode]} of ${of}`;
  if (times?.source === 'polygon') return `${base} (≈ from the travel-time map)`;
  if (times?.source === 'geometric') return `${base} (≈ estimated from distance)`;
  return base;
}
